import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import { billTypeMap } from '../../util/billingConsts'
import { activityTypes } from '../../util/consts'
import { statusTypeMap } from '../../util/foiConsts'
import { attachmentStatusDropdownOptions } from '../../util/attachmentConsts'
import { FlexColumn, FlexRow } from '../styles/Containers'
import { BoldMedBodyText, MedBodyText } from '../styles/Text'
import AssignedUserChip from '../common/AssignedUserChip'
import FOIStatus from '../foiResponse/FOIStatus'
import { parseStatusChangeActivityName } from '../../util/activitySerializer'

const AssignedUserRow = styled(FlexRow)`
  align-items: center;
  flex-wrap: wrap;
`

function parseAssignedUserId(name) {
  const match = name.match(/^Assigned to \{ id: (.+) \}$/)
  if (!match) return undefined

  const id = match[1].trim()
  if (id === 'null') return null

  return id
}

function parseLetterAssignedUser(name) {
  const match = name.match(/^(?:Letter draft|Draft): (.+) (?:Assigned|assigned) to \{ id: (.+) \}$/)
  if (!match) return undefined

  const userId = match[2].trim()
  return {
    draftName: match[1].trim(),
    userId: userId === 'null' ? null : userId
  }
}

function findUserById(users, userId) {
  return users.find(user => user.id === userId)
}

function renderStatusChange(name) {
  const { status, statusNote, rawName } = parseStatusChangeActivityName(name)
  const isKnownStatus = status && statusTypeMap[status]

  return (
    <FlexColumn gap="3px">
      <BoldMedBodyText>Status changed to</BoldMedBodyText>
      {isKnownStatus ? <FOIStatus status={status} /> : <MedBodyText>{rawName}</MedBodyText>}
      {isKnownStatus && statusNote && (
        <MedBodyText>
          <BoldMedBodyText as="span">Note:</BoldMedBodyText> {statusNote}
        </MedBodyText>
      )}
    </FlexColumn>
  )
}

function renderLetterDraftStatusChange(name) {
  const { status, fields, rawName } = parseStatusChangeActivityName(name)
  const statusOption = attachmentStatusDropdownOptions.find(option => option.value === status)
  const statusLabel = statusOption?.label || status

  if (!statusLabel) return renderDefault(rawName)

  return (
    <FlexColumn gap="3px">
      <BoldMedBodyText>Letter draft status changed to</BoldMedBodyText>
      <MedBodyText>{statusLabel}</MedBodyText>
      {fields.draftName && (
        <MedBodyText>
          <BoldMedBodyText as="span">Draft:</BoldMedBodyText> {fields.draftName}
        </MedBodyText>
      )}
    </FlexColumn>
  )
}

function renderDueDatePaused(name) {
  return (
    <FlexColumn gap="3px">
      <BoldMedBodyText>Due Date Paused -</BoldMedBodyText>
      <FOIStatus status={name} />
    </FlexColumn>
  )
}

function renderDueDateUnpaused(name) {
  return (
    <FlexColumn gap="3px">
      <BoldMedBodyText>Due Date Unpaused -</BoldMedBodyText>
      <FOIStatus status={name} />
    </FlexColumn>
  )
}

function renderDueDateChange(name) {
  if (name.startsWith('Due Date Paused - ')) {
    const status = name.replace('Due Date Paused - ', '').trim()
    if (statusTypeMap[status]) {
      return (
        <FlexColumn gap="3px">
          <BoldMedBodyText>Due Date Paused -</BoldMedBodyText>
          <FOIStatus status={status} />
        </FlexColumn>
      )
    }
  }

  if (name.startsWith('Due Date Unpaused - ')) {
    const status = name.replace('Due Date Unpaused - ', '').trim()
    if (statusTypeMap[status]) {
      return (
        <FlexColumn gap="3px">
          <BoldMedBodyText>Due Date Unpaused -</BoldMedBodyText>
          <FOIStatus status={status} />
        </FlexColumn>
      )
    }
  }

  if (statusTypeMap[name]) {
    return (
      <FlexColumn gap="3px">
        <BoldMedBodyText>Due Date Unpaused -</BoldMedBodyText>
        <FOIStatus status={name} />
      </FlexColumn>
    )
  }

  if (name.startsWith('PAUSED_')) {
    const status = name.replace('PAUSED_', '')
    if (statusTypeMap[status]) {
      return (
        <FlexColumn gap="3px">
          <BoldMedBodyText>Due Date Paused -</BoldMedBodyText>
          <FOIStatus status={status} />
        </FlexColumn>
      )
    }
  }

  if (name.startsWith('UNPAUSED_')) {
    const status = name.replace('UNPAUSED_', '')
    if (statusTypeMap[status]) {
      return (
        <FlexColumn gap="3px">
          <BoldMedBodyText>Due Date Unpaused -</BoldMedBodyText>
          <FOIStatus status={status} />
        </FlexColumn>
      )
    }
  }

  return <BoldMedBodyText>{name}</BoldMedBodyText>
}

function renderBillCreated(name) {
  const regex = new RegExp(`(${Object.keys(billTypeMap).join('|')})`, 'i')
  const parts = name.split(regex)
  return (
    <BoldMedBodyText>
      {parts[0]}
      {billTypeMap[parts[1]]}
      {parts[2]}
    </BoldMedBodyText>
  )
}

function renderResponseLinkResent(name) {
  return <BoldMedBodyText>{name}</BoldMedBodyText>
}

function renderAssignedUserUpdated(name, users) {
  const userId = parseAssignedUserId(name)

  if (userId === null) {
    return <BoldMedBodyText>Unassigned</BoldMedBodyText>
  }

  const user = userId ? findUserById(users, userId) : null

  return (
    <AssignedUserRow gap="5px">
      <BoldMedBodyText>Assigned to</BoldMedBodyText>
      <AssignedUserChip firstName={user?.firstName} lastName={user?.lastName} color={user?.chipColor} showIcon={false} />
    </AssignedUserRow>
  )
}

function renderLetterAssignedUserUpdated(name, users) {
  const parsedActivity = parseLetterAssignedUser(name)
  if (!parsedActivity) return renderAssignedUserUpdated(name, users)

  const user = parsedActivity.userId ? findUserById(users, parsedActivity.userId) : null
  const isUnassigned = parsedActivity.userId === null

  return (
    <FlexColumn gap="3px">
      <MedBodyText>
        <BoldMedBodyText as="span">Letter draft:</BoldMedBodyText> {parsedActivity.draftName}
      </MedBodyText>
      <AssignedUserRow gap="5px">
        <BoldMedBodyText>{isUnassigned ? 'Unassigned' : 'Assigned to'}</BoldMedBodyText>
        {user && <AssignedUserChip firstName={user.firstName} lastName={user.lastName} color={user.chipColor} showIcon={false} />}
      </AssignedUserRow>
    </FlexColumn>
  )
}

function renderDefault(name) {
  return <BoldMedBodyText>{name}</BoldMedBodyText>
}

const ActivityLogItemContent = ({ type, name, users = [] }) => {
  switch (type) {
    case 'STATUS_CHANGE':
      return renderStatusChange(name)
    case 'DUE_DATE_PAUSED':
      return renderDueDatePaused(name)
    case 'DUE_DATE_UNPAUSED':
      return renderDueDateUnpaused(name)
    case 'DUE_DATE_CHANGE':
      return renderDueDateChange(name)
    case 'BILL_CREATED':
      return renderBillCreated(name)
    case 'RESPONSE_LINK_RESENT':
      return renderResponseLinkResent(name)
    case 'LETTER_DRAFT_STATUS_CHANGE':
      return renderLetterDraftStatusChange(name)
    case 'LETTER_ASSIGNED_USER_UPDATED':
      return renderLetterAssignedUserUpdated(name, users)
    case 'FOI_ASSIGNED_USER_UPDATED':
      return renderAssignedUserUpdated(name, users)
    default:
      return renderDefault(name)
  }
}

ActivityLogItemContent.propTypes = {
  type: PropTypes.oneOf(activityTypes),
  name: PropTypes.string.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      chipColor: PropTypes.string
    })
  )
}

export default ActivityLogItemContent
